import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { LoaderCircle, Plus } from 'lucide-react';

import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/services/utils';

export function ManualCardDialog({
  open,
  creating,
  error,
  onOpenChange,
  onSubmit,
}: {
  open: boolean;
  creating: boolean;
  error: string;
  onOpenChange: (open: boolean) => void;
  onSubmit: (input: { word: string; translation?: string; definition?: string }) => void;
}) {
  const [word, setWord] = useState('');
  const [definition, setDefinition] = useState('');
  const [translation, setTranslation] = useState('');
  const [showTranslation, setShowTranslation] = useState(false);
  const translationInputRef = useRef<HTMLInputElement>(null);
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    if (open) {
      setWord('');
      setDefinition('');
      setTranslation('');
      setShowTranslation(false);
    }
  }, [open]);

  const canSubmit = word.trim().length > 0 && (definition.trim().length > 0 || translation.trim().length > 0);

  const handleSubmit = () => {
    if (!canSubmit || creating) return;
    onSubmit({
      word: word.trim(),
      translation: translation.trim() || undefined,
      definition: definition.trim() || undefined,
    });
  };

  const handleShowTranslation = () => {
    setShowTranslation(true);
    requestAnimationFrame(() => translationInputRef.current?.focus());
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !creating && onOpenChange(nextOpen)}>
      <DialogContent
        showCloseButton={!creating}
        className="top-[8dvh] translate-y-0 gap-0 overflow-hidden rounded-2xl border-white bg-white p-0 sm:top-1/2 sm:max-w-2xl sm:-translate-y-1/2"
      >
        <DialogTitle className="sr-only">Create manual card</DialogTitle>
        <DialogDescription className="sr-only">
          Enter a word or phrase, its definition, and an optional translation.
        </DialogDescription>

        <div className="px-5 pt-6 pb-2">
          <input
            value={word}
            onChange={(event) => setWord(event.target.value)}
            placeholder="Word or phrase"
            disabled={creating}
            autoFocus
            className="w-full border-0 bg-transparent text-2xl font-bold text-slate-900 outline-none placeholder:text-slate-300"
          />
        </div>

        <div className="border-t border-slate-100 px-5 py-4">
          <span className="block text-sm font-semibold uppercase tracking-[0.18em] text-slate-400">
            Definition
          </span>
          <textarea
            value={definition}
            onChange={(event) => setDefinition(event.target.value)}
            placeholder="Enter a definition…"
            disabled={creating}
            rows={3}
            className="mt-2 w-full resize-none border-0 bg-transparent text-[15px] leading-6 text-slate-900 outline-none placeholder:text-slate-400"
          />

          <AnimatePresence initial={false}>
            {showTranslation ? (
              <motion.div
                initial={reduceMotion ? undefined : { opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={reduceMotion ? undefined : { opacity: 0, height: 0 }}
                transition={{ duration: 0.18 }}
                className="overflow-hidden"
              >
                <div className="mt-3 border-t border-slate-100 pt-3">
                  <span className="block text-sm font-semibold uppercase tracking-[0.18em] text-slate-400">
                    Translation
                  </span>
                  <input
                    ref={translationInputRef}
                    value={translation}
                    onChange={(event) => setTranslation(event.target.value)}
                    placeholder="Translation"
                    disabled={creating}
                    className="mt-2 w-full border-0 bg-transparent text-[15px] leading-6 text-slate-900 outline-none placeholder:text-slate-400"
                  />
                </div>
              </motion.div>
            ) : (
              <div className="-mx-5 mt-3 border-t border-slate-200 px-5 pt-3">
                <button
                  type="button"
                  onClick={handleShowTranslation}
                  disabled={creating}
                  className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-500 transition hover:bg-slate-50"
                >
                  <Plus className="size-3.5" />
                  Add translation
                </button>
              </div>
            )}
          </AnimatePresence>
        </div>

        {error && (
          <div role="alert" className="border-t border-red-100 bg-red-50 px-5 py-2.5 text-xs text-red-700">
            {error}
          </div>
        )}

        <div className="flex items-center justify-end border-t border-slate-200 bg-slate-100 px-5 py-3">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit || creating}
            className={cn(
              'flex h-10 items-center gap-2 rounded-full bg-slate-950 px-5 text-sm font-semibold text-white transition hover:bg-slate-800',
              (!canSubmit || creating) && 'pointer-events-none opacity-50'
            )}
          >
            {creating && <LoaderCircle className="size-4 animate-spin" />}
            Create card
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
